import { isViewerRole } from "@shared/writer-roles";
import { useQuery } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext, useEffect } from "react";
import { identifyUser, registerSuperProperties, resetUser, trackEvent } from "./analytics";
import { apiRequest, cancelPendingRateLimitRecoveries, queryClient } from "./queryClient";
import { destroySSEClient } from "./sseClient";

type AuthUser = {
  id: string;
  email: string;
  fullName: string;
  workspaceId: string;
  role: string;
  firstLoginPending?: boolean;
  supportMode?: { workspaceId: string; workspaceName: string; };
};

type AuthContextType = {
  user: AuthUser | null;
  isLoading: boolean;
  /**
   * True while the most recent `/api/auth/me` check failed with a 429
   * (OCT-10784) rather than a confirmed 401. A throttled check is NOT
   * evidence the user is logged out — the session may still be perfectly
   * valid — so callers (e.g. `App.tsx`'s `AppContent`) should keep showing a
   * loading state rather than the login page while this is true, instead of
   * collapsing a transient rate-limit into a false "logged out" render.
   */
  isAuthCheckThrottled: boolean;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | null>(null);

/** @spec ILLEGAL — a throttled auth check must never be treated as a confirmed logout (OCT-10784). */
class AuthCheckThrottledError extends Error {
  constructor() {
    super("Auth check rate-limited");
    this.name = "AuthCheckThrottledError";
  }
}

export function AuthProvider({ children }: { children: ReactNode; }) {
  const { data: user, isLoading, isError, error } = useQuery<AuthUser | null>({
    queryKey: ["/api/auth/me"],
    queryFn: async () => {
      try {
        const res = await fetch("/api/auth/me", { credentials: "include" });
        if (res.status === 401) return null;
        if (res.status === 429) throw new AuthCheckThrottledError();
        if (!res.ok) throw new Error(`Auth check failed: ${res.status}`);
        return res.json();
      } catch (err) {
        if (err instanceof AuthCheckThrottledError) throw err;
        if (err instanceof Error && err.message.startsWith("Auth check failed")) throw err;
        throw new Error("Network error during auth check");
      }
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
    // OCT-10784: retry a throttled check more patiently than a genuine
    // failure — it's expected to clear on its own shortly.
    retry: (failureCount, err) => failureCount < (err instanceof AuthCheckThrottledError ? 4 : 1),
    retryDelay: 2000,
  });
  const isAuthCheckThrottled = isError && error instanceof AuthCheckThrottledError;

  useEffect(() => {
    if (user) {
      identifyUser(user, "core");

      const lastActiveKey = `octa-last-active-date-${user.workspaceId}`;
      const lastActive = localStorage.getItem(lastActiveKey);
      const now = Date.now();

      if (lastActive) {
        const elapsed = now - parseInt(lastActive, 10);
        const ONE_DAY = 24 * 60 * 60 * 1000;
        const SEVEN_DAYS = 7 * ONE_DAY;

        if (elapsed > SEVEN_DAYS) {
          trackEvent("Dormant User Returned", {
            days_since_last_visit: Math.floor(elapsed / ONE_DAY),
          });
        } else if (elapsed > ONE_DAY) {
          trackEvent("Returning User", {
            days_since_last_visit: Math.floor(elapsed / ONE_DAY),
          });
        }
      }

      try {
        localStorage.setItem(lastActiveKey, String(now));
      } catch {}
      registerSuperProperties({ last_active_date: new Date().toISOString() });
    }
    // NOTE: do NOT resetUser() when `user === null`. The Mixpanel cookie is
    // cross-subdomain (`.weareocta.com`, shared with weareocta.com and OCTA
    // Flow), so mixpanel.reset() here would mint a fresh $device_id and
    // overwrite the identity the visitor already carries from marketing/Flow —
    // every anonymous Core page load (and every bfcache restore, which nulls
    // the auth cache below) would look like a brand-new person.
    // Identity teardown belongs to an explicit logout only — see logout().
  }, [user, isLoading]);

  useEffect(() => {
    if (!isLoading && user === null && window.location.pathname !== "/") {
      window.location.replace("/");
    }
  }, [user, isLoading]);

  useEffect(() => {
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        queryClient.setQueryData(["/api/auth/me"], null);
        queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  const logout = async () => {
    resetUser();
    // Tear down the long-lived SSE singleton explicitly before navigating.
    // Even though the subsequent full-page `window.location.replace` would
    // collect it, destroying it here guarantees the connection and its
    // reconnect backoff timers cannot leak into a re-authenticated session.
    destroySSEClient();
    const res = await apiRequest("POST", "/api/auth/logout");
    const data = await res.json();
    queryClient.setQueryData(["/api/auth/me"], null);
    queryClient.clear();
    // OCT-11301: the throttle-recovery timers are module state, not cache
    // state, so clearing the cache does not disarm them. Tear them down with
    // it so nothing refetches into the next session.
    cancelPendingRateLimitRecoveries();
    try {
      localStorage.removeItem("octa-active-entity");
    } catch {} /* storage cleanup — intentional */
    try {
      const channel = new BroadcastChannel("octa-entity-sync");
      channel.postMessage({ type: "auth-change" });
      channel.close();
    } catch {}
    if (data.logoutUrl) {
      window.location.replace(data.logoutUrl);
    } else {
      window.location.replace("/");
    }
  };

  return (
    <AuthContext.Provider value={{ user: user ?? null, isLoading, isAuthCheckThrottled, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

/**
 * OCT-17302 - false only when the signed-in user is a Viewer.
 *
 * The one front-end reading of "can this user change workspace data". It uses
 * the same shared `isViewerRole` predicate as the server's central Viewer check,
 * so the two can never disagree. The rule targets the Viewer role explicitly:
 * the legacy `member` role and every writer role see write controls exactly as
 * before OCT-16925. A signed-out or still-loading session (no user) is not
 * a writer either, so write controls never flash in before the role is known.
 * Pages hide create, edit, delete, run, approve and send controls when this
 * is false.
 *
 * @spec ILLEGAL E157
 */
export function useCanWrite(): boolean {
  const { user } = useAuth();
  return !!user && !isViewerRole(user.role);
}
